import React, { useMemo } from 'react';

import { Center, Group, Loader, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconTrendingUp } from '@tabler/icons-react';
import HelpButton from 'components/HelpButton';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { UpcomingRevenueProjection } from 'types/generated';

import ProjectedRevenueByMonthChartsPanel from './ProjectedRevenueByMonthChartsPanel';
import { RevenueProjectionDateRangeFormValues } from './types';

interface UpcomingRevenueProjectionResultProps {
  dateRangeValues: RevenueProjectionDateRangeFormValues;
  revenueProjectionData?: UpcomingRevenueProjection;
  isLoadingOrFetching: boolean;
}

const UpcomingRevenueProjectionResult = ({
  dateRangeValues,
  revenueProjectionData,
  isLoadingOrFetching,
}: UpcomingRevenueProjectionResultProps): JSX.Element => {
  const { formatCurrency } = useLocalization();

  const formatDateRange = (): string => {
    if (!dateRangeValues.startDate || !dateRangeValues.endDate) {
      return '';
    }

    const startMonth = dayjs(dateRangeValues.startDate).format('MMMM YYYY');
    const endMonth = dayjs(dateRangeValues.endDate).format('MMMM YYYY');

    if (startMonth === endMonth) {
      return startMonth;
    }

    return `${startMonth} through ${endMonth}`;
  };

  const monthsCount = useMemo((): number => {
    if (!dateRangeValues.startDate || !dateRangeValues.endDate) {
      return 0;
    }

    return dayjs(dateRangeValues.endDate).diff(dayjs(dateRangeValues.startDate), 'months') + 1;
  }, [dateRangeValues.endDate, dateRangeValues.startDate]);

  const formattedDateRange = formatDateRange();

  const isMultipleMonthsSelected = monthsCount > 1;

  return (
    <Stack>
      <Text ta='center' size='md' fw={500} tt='uppercase' c='dark.3'>
        {isMultipleMonthsSelected ? 'Selected Date Range' : 'Selected Month'}
      </Text>
      <Stack spacing={0}>
        <Text ta='center' size='xl' fw={600} c='kiloBlue'>
          {formattedDateRange}
        </Text>
        {!isMultipleMonthsSelected && (
          <Text ta='center' c='dimmed'>
            (One Month)
          </Text>
        )}
      </Stack>

      {isLoadingOrFetching && (
        <>
          <Center mt='xl' pt='xl'>
            <Loader size='xl' variant='bars' />
          </Center>
          <Text my='md' c='dimmed' ta='center'>
            Generating your projected revenue. This may take a moment.
          </Text>
        </>
      )}

      {!isLoadingOrFetching && revenueProjectionData && (
        <>
          <Text my='md' c='dimmed'>
            {monthsCount === 1 ? (
              <>
                This is the total projected revenue for{' '}
                <Text span c='dark.3' weight={600}>
                  {formattedDateRange}
                </Text>
                , based on active auto-renew packages and existing paid and unpaid invoices.
              </>
            ) : (
              <>
                This total represents the combined projected revenue across all{' '}
                <Text span c='dark.3' weight={600}>
                  {monthsCount} months
                </Text>{' '}
                in your selected period, from existing paid and unpaid invoices and auto-renewing packages.
              </>
            )}
          </Text>

          <Paper bg='gray.1' p='xl'>
            <Group position='apart'>
              <Stack spacing='xs'>
                <Group>
                  <Text size='md' color='dark.3' fw={600}>
                    Total Projected Revenue
                  </Text>
                  <HelpButton width={300} size='xs' position='right' withinPortal>
                    Combines paid and unpaid existing invoices and projected invoices from packages with
                    Auto-renew enabled for your selected date range.
                  </HelpButton>
                </Group>
                <Title color='green'>{formatCurrency(Number(revenueProjectionData.total))}</Title>
              </Stack>

              <Stack mr='xl'>
                <ThemeIcon radius='xl' size='46px' color='green'>
                  <IconTrendingUp />
                </ThemeIcon>
              </Stack>
            </Group>
          </Paper>

          {isMultipleMonthsSelected && (
            <ProjectedRevenueByMonthChartsPanel revenueProjectionData={revenueProjectionData} />
          )}
        </>
      )}
    </Stack>
  );
};

export default UpcomingRevenueProjectionResult;
